import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { toast } from "sonner";

import * as ozon from "@/api/ozonSupply";
import { extractErrorMessage } from "@/lib/api-errors";
import { saveBlob } from "@/lib/download";
import { palletPlanKey } from "./ozonQueries";

/** Все вызовы раскладки — двумя наборами: правка мест и «наружу» (площадка, печать, 1С).
 *
 *  Вынесены из редактора целиком, вместе с текстами тостов: они и есть ответ пользователю, и
 *  разъехаться с кнопкой не должны. Каждый набор отдаёт сами мутации — кнопке нужен её
 *  собственный `isPending`, чтобы крутить иконку именно у нажатой.
 */

type Fail = (what: string) => (e: unknown) => void;

const failWith: Fail = (what) => (e) => {
  toast.error(extractErrorMessage(e, what));
};

export interface PlaceEditActions {
  mode: UseMutationResult<ozon.OzonDirection, unknown, boolean>;
  compute: UseMutationResult<ozon.OzonPlaces, unknown, void>;
  move: UseMutationResult<
    ozon.OzonPlaces,
    unknown,
    Parameters<typeof ozon.moveOzonItems>[1]
  >;
  gather: UseMutationResult<
    ozon.OzonPlaces,
    unknown,
    Parameters<typeof ozon.gatherOzonPlaces>[1]
  >;
  boxes: UseMutationResult<ozon.OzonPlaces, unknown, ozon.OzonPick[]>;
  drop: UseMutationResult<ozon.OzonPlaces, unknown, number[]>;
  /** полный пересбор раскладки ВНУТРИ режима изменений: места с кодами опустеют, коды сменятся */
  recompute: UseMutationResult<ozon.OzonPlaces, unknown, void>;
  /** сверить партии с живым остатком 1С и переподобрать только нехватку */
  repick: UseMutationResult<ozon.OzonPlaces, unknown, void>;
  /** правки РЕЖИМА ИЗМЕНЕНИЙ: состав после заявки растёт, уменьшается и меняет партии только ими */
  add: UseMutationResult<
    ozon.OzonPlaces,
    unknown,
    Parameters<typeof ozon.addOzonPlaceItem>[1]
  >;
  remove: UseMutationResult<
    ozon.OzonPlaces,
    unknown,
    Parameters<typeof ozon.removeOzonPlaceItem>[1]
  >;
  rebatch: UseMutationResult<
    ozon.OzonPlaces,
    unknown,
    Parameters<typeof ozon.rebatchOzonPlaceItem>[1]
  >;
  busy: boolean;
}

/** Правка раскладки: тип поставки, пересчёт, перенос, сборка, разбор, снятие кодов. */
export function useOzonPlaceEdits(
  shipmentId: number,
  strategy: string,
  opts: {
    /** положить свежую раскладку и снять выбор (внутри уже зовёт `afterLayoutChange`) */
    put: (d: ozon.OzonPlaces) => void;
    /** перечитать места И СНЯТЬ ВЫБОР — смена режима удаляет места на сервере, а ответа с
     *  раскладкой не приносит: без сброса над пустой таблицей висело «Выбрано: 1 позиция» */
    refetch: () => void;
    /** перечитать чек-лист площадки: после ухода мест её ответ другой */
    refetchRules: () => void;
    /** перечитать направление: по нему считаются стадия, шапка и главная кнопка */
    afterLayoutChange: () => void;
  },
): PlaceEditActions {
  const mode = useMutation({
    mutationFn: (on: boolean) => ozon.setOzonTransport(shipmentId, on),
    onSuccess: (d) => {
      // ⚠️ вместе с направлением: у страницы поставки тип приезжает СВОИМ запросом, и без
      // этого она осталась бы с прежним режимом — с чужими правилами раскладки на экране
      opts.afterLayoutChange();
      opts.refetch();
      toast.success(
        d.transport
          ? "Поставка с грузоместами: раскладка только паллетами, все коробки встанут на них"
          : "Обычная поставка: паллеты, короба или их сочетание",
      );
    },
    onError: failWith("Тип поставки не изменён"),
  });
  const compute = useMutation({
    mutationFn: () => ozon.computeOzonPlaces(shipmentId, strategy || undefined),
    onSuccess: opts.put,
    onError: failWith("Разложить не удалось"),
  });
  const recompute = useMutation({
    mutationFn: () =>
      ozon.recomputeOzonPlaces(shipmentId, strategy || undefined),
    onSuccess: (d) => {
      opts.put(d);
      toast.success(
        "Раскладка пересобрана заново: места с кодами опустели и ждут снятия каскадом",
      );
    },
    onError: failWith("Разложить заново не удалось"),
  });
  // ⚠️ Сводку говорим ЕЁ ЖЕ словами: «переподобрано N строк в M местах; нетронуты K мест» считает
  // бэкенд, и пересчитанная во фронте она разъехалась бы с тем, что лежит в базе. Строка с ней
  // приезжает в `notes` и остаётся на экране под таблицей — тост только поднимает её к глазам.
  const repick = useMutation({
    mutationFn: () => ozon.repickOzonPlaces(shipmentId),
    onSuccess: (d) => {
      opts.put(d);
      const summary = d.notes.find((n) => n.startsWith("Перераспределение"));
      if (summary) toast.success(summary);
    },
    onError: failWith("Перераспределить партии не удалось"),
  });
  const move = useMutation({
    mutationFn: (v: Parameters<typeof ozon.moveOzonItems>[1]) =>
      ozon.moveOzonItems(shipmentId, v),
    onSuccess: opts.put,
    onError: failWith("Перенести не удалось"),
  });
  const gather = useMutation({
    mutationFn: (v: Parameters<typeof ozon.gatherOzonPlaces>[1]) =>
      ozon.gatherOzonPlaces(shipmentId, v),
    onSuccess: opts.put,
    onError: failWith("Собрать не удалось"),
  });
  const boxes = useMutation({
    mutationFn: (picks: ozon.OzonPick[]) =>
      ozon.ozonPlacesToBoxes(shipmentId, picks),
    onSuccess: opts.put,
    onError: failWith("Разобрать на короба не удалось"),
  });
  const drop = useMutation({
    mutationFn: (positions: number[]) =>
      ozon.dropOzonCargoes(shipmentId, positions),
    onSuccess: (d) => {
      opts.put(d);
      opts.refetchRules();
    },
    onError: failWith("Коды не сняты"),
  });
  // ⚠️ Три правки ниже работают ТОЛЬКО в режиме изменений — бэкенд спрашивает заход первым
  // делом. Своей проверки «а идёт ли правка» тут нет намеренно: две проверки разъезжаются молча,
  // а отказ ручки виден тостом с её же текстом.
  const add = useMutation({
    mutationFn: (v: Parameters<typeof ozon.addOzonPlaceItem>[1]) =>
      ozon.addOzonPlaceItem(shipmentId, v),
    onSuccess: (d) => {
      opts.put(d);
      toast.success(
        "Артикул добавлен — он поедет новым местом, состав заявки вырастет",
      );
    },
    onError: failWith("Добавить артикул не удалось"),
  });
  const remove = useMutation({
    mutationFn: (v: Parameters<typeof ozon.removeOzonPlaceItem>[1]) =>
      ozon.removeOzonPlaceItem(shipmentId, v),
    onSuccess: opts.put,
    onError: failWith("Убрать не удалось"),
  });
  const rebatch = useMutation({
    mutationFn: (v: Parameters<typeof ozon.rebatchOzonPlaceItem>[1]) =>
      ozon.rebatchOzonPlaceItem(shipmentId, v),
    onSuccess: (d) => {
      opts.put(d);
      toast.success("Партия заменена — подбор её больше не трогает");
    },
    onError: failWith("Заменить партию не удалось"),
  });
  return {
    mode,
    compute,
    recompute,
    repick,
    move,
    gather,
    boxes,
    drop,
    add,
    remove,
    rebatch,
    busy:
      // ⚠️ смена режима удаляет ВСЕ места на сервере: пока она летит, «Отправить» гонялся бы с
      // затиранием раскладки
      mode.isPending ||
      compute.isPending ||
      recompute.isPending ||
      repick.isPending ||
      move.isPending ||
      gather.isPending ||
      boxes.isPending ||
      drop.isPending ||
      add.isPending ||
      remove.isPending ||
      rebatch.isPending,
  };
}

export interface DocActions {
  send: UseMutationResult<ozon.OzonPlaces, unknown, void>;
  reissue: UseMutationResult<ozon.OzonPlaces, unknown, void>;
  labels: UseMutationResult<Blob, unknown, void>;
  /** этикетки ТОЛЬКО мест, которые завела применённая правка (`changed_only=1`) */
  labelsChanged: UseMutationResult<Blob, unknown, void>;
  pallets: UseMutationResult<Blob, unknown, void>;
  list: UseMutationResult<Blob, unknown, void>;
  xlsx: UseMutationResult<Blob, unknown, void>;
  onecRun: UseMutationResult<ozon.OzonOnec, unknown, void>;
  /** цикл забыт — в учёте по этой поставке больше ничего, поэтому `null` в ответе */
  reset: UseMutationResult<ozon.OzonOnec | null, unknown, void>;
  busy: boolean;
}

/** Наружу: отправка мест на площадку, печать и цикл документов 1С. */
export function useOzonDocActions(
  shipmentId: number,
  supplyNumber: string | null,
  opts: {
    /** положить свежую раскладку и снять выбор (внутри уже зовёт `afterLayoutChange`) */
    put: (d: ozon.OzonPlaces) => void;
    refetchRules: () => void;
    /** перечитать направление: `d.onec` и стадия экрана читаются ТОЛЬКО из него */
    afterLayoutChange: () => void;
  },
): DocActions {
  const qc = useQueryClient();
  // PDF приходит телом ответа, а не ссылкой: файл площадки мы дополняем своей допечаткой.
  // ⚠️ Отдаём СКАЧИВАНИЕМ: `window.open` из пришедшего ответа браузер резал блокировщиком
  // всплывающих окон молча — ни файла, ни ошибки, кнопка выглядела сломанной.
  const save =
    (what: string, ext = "pdf") =>
    (blob: Blob): void => {
      const name = `ozon-${what}-${supplyNumber ?? shipmentId}.${ext}`;
      saveBlob(blob, name);
      toast.success(`${name} — скачан`);
    };
  const savePdf = (what: string): ((blob: Blob) => void) => save(what);

  const send = useMutation({
    mutationFn: () => ozon.sendOzonPlaces(shipmentId),
    onSuccess: (d) => {
      opts.put(d);
      toast.success(
        `Места отправлены: ${d.places.filter((p) => p.cargo_id).length} кодов`,
      );
      opts.refetchRules();
    },
    onError: failWith("Места не отправлены"),
  });
  // ⚠️ Отдельная кнопка, а не тихий шаг отправки: перевыпуск меняет коды У ВСЕХ мест, и
  // напечатанные этикетки становятся недействительными. Нужен он там, где партия кончилась уже
  // ПОСЛЕ отправки: вместе с партией меняется срок годности внутри коробки, а он уже уехал на
  // площадку (боевой случай 15.09.2026 — семь поставок встали на проведении ПТМО).
  const reissue = useMutation({
    mutationFn: () => ozon.reissueOzonCargoes(shipmentId),
    onSuccess: (d) => {
      opts.put(d);
      toast.success(
        `Места перевыпущены: ${d.places.filter((p) => p.cargo_id).length} новых кодов — этикетки печатайте заново`,
      );
      opts.refetchRules();
    },
    onError: failWith("Перевыпустить места не удалось"),
  });
  // ⚠️ Сброс — единственная дорога назад: заказ 1С резервирует КОНКРЕТНЫЕ партии и повторным
  // прогоном не переписывается, а бумаги на нём идемпотентны по имени файла. Переделали состав —
  // документы удаляются в 1С, цикл забывается, и поставка пишется заново.
  const reset = useMutation({
    mutationFn: () => ozon.resetOnecCycle(shipmentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: palletPlanKey(shipmentId) });
      // ⚠️ своего состояния «цикл сброшен» больше нет: что записано в 1С, знает направление, и
      // считать это вторым, сессионным способом значило бы разъехаться с ним после перезагрузки
      opts.afterLayoutChange();
      toast.success(
        "Цикл 1С сброшен — поставку можно перевыпускать и записывать заново",
      );
    },
    onError: failWith("Сбросить цикл 1С не удалось"),
  });
  // ⚠️ План паллет портала склада цикл пишет сам (`PALLETS_TARGET=portal`) — и при успехе, и при
  // отказе (записанный конфликт, тело без ответа): блок плана перечитываем в обоих случаях
  const onecRun = useMutation({
    mutationFn: () => ozon.createOnecDocuments(shipmentId),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: palletPlanKey(shipmentId) });
      opts.afterLayoutChange();
      toast.success(
        d.state === "done"
          ? `В 1С: заказ ${d.order_number}, ПТМО ${d.transfer_number}, счёт-фактура ${d.invoice_number}`
          : `1С: дошли до шага «${d.state}» — повторите, чтобы продолжить`,
      );
    },
    onError: (e) => {
      void qc.invalidateQueries({ queryKey: palletPlanKey(shipmentId) });
      opts.afterLayoutChange();
      failWith("В 1С записать не удалось")(e);
    },
  });
  const labels = useMutation({
    mutationFn: () => ozon.getOzonLabels(shipmentId),
    onSuccess: savePdf("этикетки"),
    onError: failWith("Этикетки не готовы"),
  });
  // ⚠️ Стопка ТОЛЬКО на новые места: у уцелевших наклейки давно на коробах и верны, а полная
  // стопка отправила бы склад переклеивать сотни коробов ради десятка новых. Успешная печать
  // снимает напоминание (`d.reprint` пустеет) — поэтому перечитываем направление.
  const labelsChanged = useMutation({
    mutationFn: () => ozon.getOzonChangedLabels(shipmentId),
    onSuccess: (blob) => {
      savePdf("этикетки-затронутых")(blob);
      opts.afterLayoutChange();
    },
    onError: failWith("Этикетки затронутых мест не готовы"),
  });
  const pallets = useMutation({
    mutationFn: () => ozon.getOzonPalletLabels(shipmentId),
    onSuccess: savePdf("палеты"),
    onError: failWith("Наклейки палет не готовы"),
  });
  const list = useMutation({
    mutationFn: () => ozon.getOzonPackingList(shipmentId),
    onSuccess: savePdf("сборочный-лист"),
    onError: failWith("Сборочный лист не готов"),
  });
  // тот же лист таблицей: по нему сводят состав с чужими выгрузками (ВПР, сводные)
  const xlsx = useMutation({
    mutationFn: () => ozon.getOzonPackingListXlsx(shipmentId),
    onSuccess: save("сборочный-лист", "xlsx"),
    onError: failWith("Сборочный лист (Excel) не готов"),
  });
  return {
    send,
    reissue,
    labels,
    labelsChanged,
    pallets,
    list,
    xlsx,
    onecRun,
    reset,
    busy:
      send.isPending ||
      reissue.isPending ||
      labels.isPending ||
      labelsChanged.isPending ||
      pallets.isPending ||
      list.isPending ||
      xlsx.isPending ||
      onecRun.isPending ||
      reset.isPending,
  };
}
